/**
 * ChatSheet — playground drawer for a single fixed Model.
 *
 * Pre-PMF playground only. Real chats become R2 traces via the gateway,
 * not via this sheet. No DB persistence, no session restoration — refresh
 * clears the conversation.
 *
 * Rendered once at the page level regardless of `open`, so the underlying
 * `useStreamingChat` hook instance (and its message state) survives
 * close/reopen — only the Sheet's visibility toggles.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'

import { Loader2, Send, Square, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import type { Model } from '@/hooks/useModels'
import { useStreamingChat, type Message } from '@/hooks/useStreamingChat'

interface MessageBubbleProps {
  message: Message
}

function MessageBubble({ message }: MessageBubbleProps): React.JSX.Element {
  const isUser = message.role === 'user'
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      role={isUser ? undefined : 'status'}
    >
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-4 py-2 text-sm ${
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'border border-border bg-muted/40 text-foreground'
        }`}
      >
        {message.content || (
          <span className="italic text-muted-foreground">…</span>
        )}
      </div>
    </div>
  )
}

interface ChatSheetProps {
  model: Model
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ChatSheet({ model, open, onOpenChange }: ChatSheetProps): React.JSX.Element {
  const { messages, sendMessage, isStreaming, error, stop, clearMessages } =
    useStreamingChat({
      modelId: model.id,
    })

  const [input, setInput] = useState('')

  // Pin the message thread to the bottom as new tokens stream in.
  const threadRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [messages])

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const text = input.trim()
    if (!text || isStreaming) return
    setInput('')
    await sendMessage(text)
  }

  function handleClear(): void {
    clearMessages()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl flex flex-col gap-4">
        <SheetHeader>
          <SheetTitle>Playground — {model.display_name}</SheetTitle>
          <SheetDescription>
            Stateless playground against <span className="font-mono">{model.slug}</span>.
            Refreshing clears the conversation.
          </SheetDescription>
        </SheetHeader>

        <div
          ref={threadRef}
          className="flex-1 overflow-auto rounded-md border border-border bg-card p-4"
        >
          {messages.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground">
              Start the conversation by sending a message below.
            </p>
          ) : (
            <div className="space-y-3">
              {messages.map((m, idx) => (
                <MessageBubble key={idx} message={m} />
              ))}
            </div>
          )}
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <form onSubmit={(e) => { void handleSubmit(e) }} className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => { setInput(e.target.value) }}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter newlines — the conventional chat UX.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void handleSubmit(e as unknown as FormEvent<HTMLFormElement>)
              }
            }}
            placeholder="Type a message…"
            rows={1}
            disabled={isStreaming}
            className="min-h-10 resize-none"
          />
          {isStreaming ? (
            <Button
              type="button"
              variant="outline"
              onClick={stop}
              aria-label="Stop streaming"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={!input.trim()}
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            disabled={isStreaming || messages.length === 0}
            aria-label="Clear conversation"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </form>

        {isStreaming && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Streaming response…
          </p>
        )}
      </SheetContent>
    </Sheet>
  )
}
